/**
 * GO Commerce Session Manager
 * 
 * Handles session management, including expiration detection, proactive checking,
 * and automatic redirection when a session becomes invalid.
 */

import { useCacheableBackendService } from '@/composables/services/useCacheableBackend.service';
import { navigateTo } from '#app';

// Configuration
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const SESSION_MAX_IDLE_TIME = 55 * 60 * 1000; // Consider checking after 55 minutes of inactivity (5 mins before expiry)
const SESSION_MESSAGE_DURATION = 8000; // Display toast messages for 8 seconds
const SESSION_EXPIRY_DEBOUNCE = 10000; // Debounce session expiry events (10 seconds)

// Session timeout policy: All sessions expire at 60 minutes regardless of "Remember Me" setting
const SESSION_TIMEOUT_POLICY = 60 * 60 * 1000; // 60 minutes in milliseconds

// Flag to prevent multiple checks in parallel
let sessionCheckInProgress = false;
// Track last user activity
let lastActivityTime = Date.now();
// Store timer ID for cleanup
let sessionCheckTimerId: number | null = null;
// Debounce session expiry handling
let lastSessionExpiryTime = 0;
let redirectInProgress = false;

/**
 * Initialize the session manager
 * Should be called once early in the application lifecycle
 */
export function initSessionManager() {
  if (typeof window === 'undefined') return; // Skip in SSR
  
  console.log('[SessionManager] Initializing session management');
  console.log('[SessionManager] Session timeout policy: All sessions expire at 60 minutes');
  console.log('[SessionManager] Proactive session check after 55 minutes of inactivity');
  
  // Set up event listener for session expiration
  window.addEventListener('go:session-expired', handleSessionExpired);
  
  // Track user activity to detect idle periods
  trackUserActivity();
  
  // Set up periodic session checks
  startPeriodicSessionCheck();
  
  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    if (sessionCheckTimerId) {
      clearInterval(sessionCheckTimerId);
    }
  });
}

/**
 * Tracks user activity to reset idle timer
 */
function trackUserActivity() {
  if (typeof window === 'undefined') return;
  
  // Update last activity timestamp on user interaction
  const activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
  
  activityEvents.forEach(eventType => {
    window.addEventListener(eventType, () => {
      lastActivityTime = Date.now();
    });
  });
}

/**
 * Start periodic checks for session validity
 */
function startPeriodicSessionCheck() {
  if (typeof window === 'undefined') return;
  
  // Clear any existing timer
  if (sessionCheckTimerId) {
    clearInterval(sessionCheckTimerId);
  }
  
  // Set up new timer
  sessionCheckTimerId = window.setInterval(() => {
    const idleTime = Date.now() - lastActivityTime;
    
    // Only check session if user has been idle for a while and we're not already checking
    if (idleTime > SESSION_MAX_IDLE_TIME && !sessionCheckInProgress) {
      console.log(`[SessionManager] User idle for ${Math.round(idleTime/1000)} seconds, checking session...`);
      checkSessionValidity();
    }
  }, SESSION_CHECK_INTERVAL) as unknown as number;
  
  // Store timer ID in window for debugging/cleanup
  if (typeof window !== 'undefined') {
    window.__goSessionExpiryTimerId = sessionCheckTimerId;
  }
}

/**
 * Check if the current session is still valid
 * Makes a lightweight API call that will fail if session is invalid
 */
export async function checkSessionValidity() {
  if (typeof window === 'undefined' || sessionCheckInProgress) return;
  
  sessionCheckInProgress = true;
  console.log('[SessionManager] Checking session validity...');
  
  try {
    // Use the backend service to make a session check
    const backend = useCacheableBackendService();
    
    // Force a fresh request to bypass cache
    await backend.getSession(true);
    
    // If we get here, session is valid
    console.log('[SessionManager] Session check passed - session is valid');
    sessionCheckInProgress = false;
    return true;
  } catch (error) {
    console.warn('[SessionManager] Session check failed - session may be invalid', error);
    
    // If the check fails, treat it as a session expiration
    const sessionExpiredEvent = new CustomEvent('go:session-expired', {
      detail: { 
        reason: 'session_check_failed',
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(sessionExpiredEvent);
    
    sessionCheckInProgress = false;
    return false;
  }
}

/**
 * Handle session expiration events
 * Clears cache, shows a message, and redirects to account page
 * Implements debouncing to prevent multiple redirects
 */
export function handleSessionExpired(event: Event) {
  if (typeof window === 'undefined') return;
  
  const now = Date.now();
  const customEvent = event as CustomEvent;
  const reason = customEvent?.detail?.reason || 'unknown';
  
  // Implement debouncing to prevent multiple session expiry events in short succession
  if (now - lastSessionExpiryTime < SESSION_EXPIRY_DEBOUNCE) {
    console.log(`[SessionManager] Ignoring duplicate session expiry event (debounced), reason: ${reason}`);
    return;
  }
  
  // Update last session expiry time
  lastSessionExpiryTime = now;
  
  // Prevent multiple redirects
  if (redirectInProgress) {
    console.log('[SessionManager] Redirect already in progress, ignoring');
    return;
  }
  
  console.warn(`[SessionManager] Session expired (reason: ${reason})`);
  
  // 1. Clear auth-related cache and storage
  clearAuthData();
  
  // 2. Show a user-friendly message - now silent to avoid multiple alerts
  showSessionExpiredMessage();
  
  // 3. Set redirect flag to prevent multiple redirects
  redirectInProgress = true;
  
  // Only redirect once with a slightly longer delay to ensure cleanup completes
  setTimeout(() => {
    console.log('[SessionManager] Redirecting to account page...');
    
    // Force direct redirect to account page without going through redirectToLogin 
    // to avoid any potential routing conflicts
    try {
      navigateTo('/account');
    } catch (error) {
      console.log('[SessionManager] Direct navigation failed, using location.href');
      window.location.href = '/account';
    }
    
    // Reset the redirect flag after a longer delay
    setTimeout(() => {
      redirectInProgress = false;
    }, SESSION_EXPIRY_DEBOUNCE);
  }, 800); // Slightly longer delay to ensure proper cleanup
}

/**
 * Clear all auth-related data from cache and storage
 */
function clearAuthData() {
  try {
    // Clear specific cache entries
    const backend = useCacheableBackendService();
    backend.invalidateCache('user-profile');
    
    // Clear any auth tokens from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    
    // Remove auth cookies by setting them to expire
    // We don't know the exact cookie names, so this is a best effort
    document.cookie.split(';').forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      if (
        cookieName.includes('token') || 
        cookieName.includes('auth') || 
        cookieName.includes('session')
      ) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    });
    
    console.log('[SessionManager] Cleared auth data');
  } catch (error) {
    console.error('[SessionManager] Error clearing auth data:', error);
  }
}

/**
 * Show a user-friendly session expired message
 * Note: We've removed the alert box as it was showing multiple times
 */
function showSessionExpiredMessage() {
  // Silently log the session expiry without showing alerts
  // This prevents multiple popups when a session expires
  console.log('[SessionManager] Session expired - silent notification');
  
  // Removed alert box that was causing multiple popups
  // In production, you could use a toast library with proper deduplication
}

/**
 * Redirect the user to the account page since we don't have a dedicated login page
 * Includes a query parameter to indicate session expiry for proper UI handling
 */
function redirectToLogin() {
  try {
    // Redirect to /account instead of /login since there is no /login page
    // Include session_expired=true query parameter to indicate reason for redirection
    console.log('[SessionManager] Redirecting to account page with session_expired flag');
    navigateTo('/account?session_expired=true');
  } catch (error) {
    // Fallback to direct location change
    console.log('[SessionManager] Fallback redirect to account page');
    window.location.href = '/account?session_expired=true';
  }
}

// Export public API
export default {
  init: initSessionManager,
  checkSession: checkSessionValidity,
  handleExpiration: handleSessionExpired
};
